/**
 * AI Usage — every LLM call ValidAIte makes, grouped into runs (one run per user action that
 * called an LLM, across the Node backend and the Python services).
 *
 * Start page: projects with calls and cost per gate. ?project=<id> opens one project, gate by
 * gate, with its runs; a run opens in a drawer with each call's full prompt and response.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchLlmCall,
  fetchLlmProjects,
  fetchLlmRun,
  fetchLlmRuns,
  fetchObservabilityStatus,
} from "../../services/observabilityClient";

const FONT = "Lato, sans-serif";
const CARD_SX = { bgcolor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "12px" };
const LABEL_SX = {
  fontFamily: FONT,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  color: "#94A3B8",
};
const TITLE_SX = { fontFamily: FONT, fontSize: 18, fontWeight: 800, color: "#0F172A" };
const SUBTITLE_SX = { fontFamily: FONT, fontSize: 14, color: "#64748B", mt: 0.75 };
const MONO_SX = { fontFamily: "Consolas, Menlo, monospace", fontSize: 12 };
const CELL_SX = { fontFamily: FONT, fontSize: 13, color: "#0F172A" };
const HEAD_SX = { ...LABEL_SX, py: 1 };

const PERIODS = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 0, label: "All retained" },
];

function formatUsd(v) {
  if (v == null) return "—";
  if (v === 0) return "$0";
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

const formatInt = (v) => (v == null ? "—" : Number(v).toLocaleString());

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatMs(ms) {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function statusChip(status) {
  if (status === "error") return { label: "Error", bgcolor: "#FEE2E2", color: "#B91C1C" };
  if (status === "unrecorded") return { label: "Unrecorded", bgcolor: "#FEE2E2", color: "#B91C1C" };
  if (status === "running") return { label: "Running", bgcolor: "#DBEAFE", color: "#005BF0" };
  return { label: "OK", bgcolor: "#DCFCE7", color: "#15803D" };
}

function StatusChip({ status }) {
  const chip = statusChip(status);
  return (
    <Chip
      size="small"
      label={chip.label}
      sx={{ fontFamily: FONT, fontWeight: 700, bgcolor: chip.bgcolor, color: chip.color, height: 22 }}
    />
  );
}

const SERVICE_LABELS = {
  node: "Backend",
  "intake-agent": "Intake agent",
  "metrics-eval": "Gate 2 scoring",
  "api-agent": "API agent",
  "legacy-intake": "Legacy intake",
};

/** Which service made the call: the Node backend or one of the Python services. */
function ServiceChip({ service }) {
  const key = service || "node";
  const python = key !== "node";
  return (
    <Chip
      size="small"
      label={SERVICE_LABELS[key] || key}
      sx={{
        mr: 0.75,
        height: 20,
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: python ? "#F3E8FF" : "#EFF6FF",
        color: python ? "#7E22CE" : "#005BF0",
      }}
    />
  );
}

function Kpi({ label, value, note }) {
  return (
    <Box sx={{ ...CARD_SX, p: 2, flex: "1 1 160px", minWidth: 0 }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: "#0F172A", mt: 0.5 }}>
        {value}
      </Typography>
      {note ? <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#B45309", mt: 0.5 }}>{note}</Typography> : null}
    </Box>
  );
}

function TextBlock({ label, text }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Box
        component="pre"
        sx={{
          ...MONO_SX,
          m: 0,
          mt: 0.5,
          p: 1.5,
          bgcolor: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 360,
          overflowY: "auto",
        }}
      >
        {text || "—"}
      </Box>
    </Box>
  );
}

/** Full record of one call: request messages, response, usage. */
function CallDetail({ call }) {
  const req = call.request || {};
  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#64748B" }}>
        {SERVICE_LABELS[call.service || "node"] || call.service} ·{" "}
        {call.caller ? `${call.caller.file}:${call.caller.line}` : "Caller unknown"}
        {call.model_requested && !String(call.model).startsWith(call.model_requested)
          ? ` · requested ${call.model_requested} → ran ${call.model} (model override)`
          : ""}
        {req.temperature != null ? ` · temperature ${req.temperature}` : ""}
        {req.max_tokens != null ? ` · max tokens ${req.max_tokens}` : ""}
      </Typography>
      {call.kind === "unrecorded" ? (
        <Typography sx={{ ...CELL_SX, mt: 1, color: "#B91C1C" }}>
          An LLM request to {call.provider} was sent without going through the recorder, so its prompt, tokens and
          cost were not captured. Route this call through the shared LLM service.
        </Typography>
      ) : call.kind === "chat" ? (
        <>
          {(req.messages || []).map((m, i) => (
            <TextBlock
              key={i}
              label={`${m.role} message`}
              text={typeof m.content === "string" ? m.content : JSON.stringify(m.content, null, 2)}
            />
          ))}
          <TextBlock
            label={`Response${call.response?.finish_reason ? ` (finish: ${call.response.finish_reason})` : ""}`}
            text={call.response?.content}
          />
        </>
      ) : call.kind === "moderation" ? (
        <>
          <TextBlock label="Moderated text" text={typeof req.input === "string" ? req.input : JSON.stringify(req.input, null, 2)} />
          <Typography sx={{ ...CELL_SX, mt: 1 }}>
            Flagged: {(call.response?.flagged || []).map((f) => (f ? "yes" : "no")).join(", ") || "—"} (moderation is free)
          </Typography>
        </>
      ) : (
        <Typography sx={{ ...CELL_SX, mt: 1 }}>
          Embedded {formatInt(req.inputs)} text(s) → {formatInt(call.response?.vectors)} vector(s)
        </Typography>
      )}
      {call.error ? <TextBlock label="Error" text={`${call.error.type}: ${call.error.message}`} /> : null}
    </Box>
  );
}

function RunDrawer({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const [openSeq, setOpenSeq] = useState(null);
  const [calls, setCalls] = useState({});

  useEffect(() => {
    if (!runId) return;
    setRun(null);
    setError("");
    setOpenSeq(null);
    setCalls({});
    fetchLlmRun(runId)
      .then((res) => (res?.success ? setRun(res.data) : setError(res?.error || "Failed to load run.")))
      .catch((err) => setError(err.response?.data?.error || err.message || "Failed to load run."));
  }, [runId]);

  // A run can hold calls from several services (Node + Python), each numbered within its own run.
  const callKey = (c) => `${c.run_id || runId}:${c.seq}`;

  const toggleCall = async (c) => {
    const key = callKey(c);
    if (openSeq === key) {
      setOpenSeq(null);
      return;
    }
    setOpenSeq(key);
    if (calls[key]) return;
    try {
      const res = await fetchLlmCall(c.run_id || runId, c.seq);
      setCalls((prev) => ({ ...prev, [key]: res?.success ? res.data : { error: { type: "Error", message: res?.error } } }));
    } catch (err) {
      setCalls((prev) => ({ ...prev, [key]: { error: { type: "Error", message: err.message } } }));
    }
  };

  const t = run?.totals;
  return (
    <Drawer anchor="right" open={Boolean(runId)} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 860 } } }}>
      <Box sx={{ p: 3, borderBottom: "1px solid #E6E6E6", display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...TITLE_SX, ...MONO_SX, fontSize: 15, fontWeight: 700 }}>{run?.flow || runId}</Typography>
          <Typography sx={SUBTITLE_SX}>
            {run
              ? `${formatWhen(run.started_at)} · ${formatMs(run.duration_ms)} · project ${run.project_id ?? "—"} · ${run.user || "unknown user"} · run ${run.run_id}`
              : "Loading…"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      {error ? <Typography sx={{ p: 3, color: "#DE350B", fontFamily: FONT }}>{error}</Typography> : null}
      {!run && !error ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}
      {run ? (
        <Box sx={{ p: 3, overflowY: "auto" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Kpi label="LLM calls" value={formatInt(t.calls)} note={t.errors ? `${t.errors} failed` : null} />
            {t.unrecorded ? <Kpi label="Unrecorded LLM requests" value={formatInt(t.unrecorded)} note="Sent outside the recorder" /> : null}
            <Kpi label="Tokens in / out" value={`${formatInt(t.input_tokens)} / ${formatInt(t.output_tokens)}`} />
            <Kpi
              label="Cost"
              value={formatUsd(t.cost_usd)}
              note={t.unpriced_calls ? `${t.unpriced_calls} call(s) on unpriced models` : null}
            />
          </Box>
          <Box sx={CARD_SX}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_SX}>#</TableCell>
                  <TableCell sx={HEAD_SX}>Purpose</TableCell>
                  <TableCell sx={HEAD_SX}>Model</TableCell>
                  <TableCell sx={{ ...HEAD_SX, whiteSpace: "nowrap" }} align="right">Tokens in / out</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Cost</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Latency</TableCell>
                  <TableCell sx={HEAD_SX}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.calls.map((c, i) => (
                  <Fragment key={callKey(c)}>
                    <TableRow hover onClick={() => toggleCall(c)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={CELL_SX}>{i + 1}</TableCell>
                      <TableCell sx={{ ...CELL_SX, ...MONO_SX }}>
                        <ServiceChip service={c.service} />
                        {c.kind === "embedding" || c.kind === "moderation" ? `[${c.kind}] ` : ""}
                        {c.purpose}
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>{c.model}</TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }} align="right">
                        {formatInt(c.input_tokens)} / {formatInt(c.output_tokens)}
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">{formatUsd(c.cost_usd)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{formatMs(c.latency_ms)}</TableCell>
                      <TableCell sx={CELL_SX}>
                        <StatusChip status={c.status} />
                        {c.finish_reason === "length" ? (
                          <Chip size="small" label="Truncated" sx={{ ml: 0.5, height: 22, fontFamily: FONT, bgcolor: "#FEF3C7", color: "#B45309" }} />
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {openSeq === callKey(c) ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, bgcolor: "#FCFDFE" }}>
                          {calls[callKey(c)] ? (
                            <CallDetail call={calls[callKey(c)]} />
                          ) : (
                            <Box sx={{ p: 2 }}>
                              <CircularProgress size={20} />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : null}
    </Drawer>
  );
}

/** Sortable columns of the projects table; `value` is what the column sorts by. */
const PROJECT_COLUMNS = [
  { key: "name", label: "Project", align: "left", value: (p) => projectLabel(p).toLowerCase() },
  { key: "project_id", label: "ID", align: "right", value: (p) => p.project_id },
  { key: "runs", label: "Runs", align: "right", value: (p) => p.runs },
  { key: "calls", label: "LLM calls", align: "right", value: (p) => p.calls },
  { key: "total_tokens", label: "Tokens", align: "right", value: (p) => p.total_tokens },
  { key: "cost_usd", label: "Cost", align: "right", value: (p) => p.cost_usd },
  { key: "last_run_at", label: "Last activity", align: "left", value: (p) => p.last_run_at },
];

function sortProjects(projects, { key, dir }) {
  const col = PROJECT_COLUMNS.find((c) => c.key === key) || PROJECT_COLUMNS[1];
  const sign = dir === "asc" ? 1 : -1;
  return [...projects].sort((a, b) => {
    // Calls not linked to a project always go last.
    if ((a.project_id == null) !== (b.project_id == null)) return a.project_id == null ? 1 : -1;
    const va = col.value(a);
    const vb = col.value(b);
    if (va === vb) return 0;
    return (va < vb ? -1 : 1) * sign;
  });
}

function PeriodSelect({ days, onChange }) {
  return (
    <TextField select size="small" label="Period" value={days} onChange={(e) => onChange(Number(e.target.value))} sx={{ minWidth: 170 }}>
      {PERIODS.map((p) => (
        <MenuItem key={p.value} value={p.value}>
          {p.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function TotalsRow({ totals }) {
  const avgPerRun = totals?.runs ? totals.cost_usd / totals.runs : null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Kpi label="Runs" value={formatInt(totals.runs)} />
      <Kpi label="LLM calls" value={formatInt(totals.calls)} note={totals.errors ? `${totals.errors} failed` : null} />
      <Kpi label="Tokens" value={formatInt(totals.total_tokens)} />
      <Kpi
        label="Cost"
        value={formatUsd(totals.cost_usd)}
        note={totals.unpriced_calls ? `${totals.unpriced_calls} call(s) on unpriced models` : null}
      />
      <Kpi label="Avg cost / run" value={formatUsd(avgPerRun)} />
      <Kpi
        label="Unrecorded LLM requests"
        value={formatInt(totals.unrecorded || 0)}
        note={totals.unrecorded ? "Sent outside the recorder; open the runs marked Unrecorded" : null}
      />
    </Box>
  );
}

function projectLabel(p) {
  if (p.project_id == null) return "Not linked to a project";
  return p.project_name || `Project ${p.project_id}`;
}

/** Start page: one row per project with its totals; every column sorts. */
function ProjectsView({ data, onOpen }) {
  const [sort, setSort] = useState({ key: "project_id", dir: "desc" });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" }));

  if (!data.projects.length) {
    return (
      <Box sx={{ ...CARD_SX, p: 3 }}>
        <Typography sx={{ ...CELL_SX, color: "#555555" }}>No LLM calls recorded for this period yet.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <TotalsRow totals={data.totals} />
      <Box sx={CARD_SX}>
        <Typography sx={{ ...TITLE_SX, fontSize: 15, p: 2, pb: 1 }}>Projects</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {PROJECT_COLUMNS.map((c) => (
                  <TableCell key={c.key} sx={{ ...HEAD_SX, whiteSpace: "nowrap" }} align={c.align} sortDirection={sort.key === c.key ? sort.dir : false}>
                    <TableSortLabel
                      active={sort.key === c.key}
                      direction={sort.key === c.key ? sort.dir : "asc"}
                      onClick={() => toggleSort(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortProjects(data.projects, sort).map((p) => (
                <TableRow key={p.project_id ?? "none"} hover onClick={() => onOpen(p.project_id)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 700 }}>{projectLabel(p)}</TableCell>
                  <TableCell sx={CELL_SX} align="right">{p.project_id ?? "—"}</TableCell>
                  <TableCell sx={CELL_SX} align="right">{formatInt(p.runs)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 700 }} align="right">{formatInt(p.calls)}</TableCell>
                  <TableCell sx={CELL_SX} align="right">{formatInt(p.total_tokens)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 700 }} align="right">{formatUsd(p.cost_usd)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>{formatWhen(p.last_run_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}

/** One gate of a project: its totals, then its runs in the order they happened. */
function GateCard({ gate, runs, onOpenRun }) {
  const t = gate.totals;
  return (
    <Box id={`gate-${gate.id}`} sx={{ ...CARD_SX, scrollMarginTop: 16 }}>
      <Box sx={{ p: 2, pb: 1, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1.5 }}>
        <Typography sx={{ ...TITLE_SX, fontSize: 15 }}>{gate.label}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#64748B" }}>
          {formatInt(t.runs)} run(s) · {formatInt(t.calls)} LLM calls · {formatInt(t.total_tokens)} tokens ·{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "#0F172A" }}>
            {formatUsd(t.cost_usd)}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_SX}>Started</TableCell>
              <TableCell sx={HEAD_SX}>Step</TableCell>
              <TableCell sx={HEAD_SX}>User</TableCell>
              <TableCell sx={HEAD_SX} align="right">LLM calls</TableCell>
              <TableCell sx={HEAD_SX} align="right">Tokens</TableCell>
              <TableCell sx={HEAD_SX} align="right">Cost</TableCell>
              <TableCell sx={HEAD_SX}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.run_id} hover onClick={() => onOpenRun(r.run_id)} sx={{ cursor: "pointer" }}>
                <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>{formatWhen(r.started_at)}</TableCell>
                <TableCell sx={CELL_SX}>
                  <Typography sx={{ ...CELL_SX, fontWeight: 700 }}>{r.step}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                    {(r.services || ["node"]).map((s) => (
                      <ServiceChip key={s} service={s} />
                    ))}
                    <Typography component="span" sx={{ ...MONO_SX, fontSize: 11, color: "#64748B" }}>
                      {r.flow}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={CELL_SX}>{r.user || "—"}</TableCell>
                <TableCell sx={CELL_SX} align="right">{formatInt(r.totals?.calls)}</TableCell>
                <TableCell sx={CELL_SX} align="right">{formatInt(r.totals?.total_tokens)}</TableCell>
                <TableCell sx={CELL_SX} align="right">{formatUsd(r.totals?.cost_usd)}</TableCell>
                <TableCell sx={CELL_SX}>
                  <StatusChip status={r.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

/** Project page: totals, then gate by gate with the runs inside each gate. */
function ProjectView({ data, onOpenRun }) {
  const runsByGate = {};
  for (const r of [...data.runs].sort((a, b) => (a.started_at < b.started_at ? -1 : 1))) {
    (runsByGate[r.gate] = runsByGate[r.gate] || []).push(r);
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <TotalsRow totals={data.totals} />
      {data.gates.length ? (
        data.gates.map((g) => <GateCard key={g.id} gate={g} runs={runsByGate[g.id] || []} onOpenRun={onOpenRun} />)
      ) : (
        <Box sx={{ ...CARD_SX, p: 3 }}>
          <Typography sx={{ ...CELL_SX, color: "#555555" }}>No LLM calls recorded for this project in this period.</Typography>
        </Box>
      )}
      {data.truncated ? (
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#B45309" }}>
          Showing the latest {data.runs.length} runs; gate totals include all runs.
        </Typography>
      ) : null}
    </Box>
  );
}

export default function AiUsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const project = searchParams.get("project"); // a project id, "none", or null for the projects list
  // Links from the gate pages ("AI-generated · See how") open one run or one gate section.
  const linkedRun = searchParams.get("run");
  const linkedGate = searchParams.get("gate");
  const [status, setStatus] = useState(null);
  const [days, setDays] = useState(linkedRun || linkedGate ? 0 : 7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openRunId, setOpenRunId] = useState(linkedRun);

  const closeRun = () => {
    setOpenRunId(null);
    if (linkedRun) {
      const next = new URLSearchParams(searchParams);
      next.delete("run");
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    if (!linkedGate || data?.view !== "project") return;
    document.getElementById(`gate-${linkedGate}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [linkedGate, data]);

  useEffect(() => {
    fetchObservabilityStatus()
      .then((res) => setStatus(res?.data || null))
      .catch(() => setStatus(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = project
        ? await fetchLlmRuns({ days, projectId: project, limit: 1000 })
        : await fetchLlmProjects({ days });
      if (!res?.success) {
        setError(res?.error || "Failed to load AI usage.");
        return;
      }
      setData({ view: project ? "project" : "projects", ...res.data });
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load AI usage.");
    } finally {
      setLoading(false);
    }
  }, [days, project]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const openProject = (id) => setSearchParams({ project: id == null ? "none" : String(id) });
  const backToProjects = () => setSearchParams({});

  const projectTitle =
    project === "none" ? "Not linked to a project" : data?.project_name || (project ? `Project ${project}` : "");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#F5F5F5", minWidth: 0 }}>
      <Box sx={{ bgcolor: "#FFFFFF", borderBottom: "1px solid #E6E6E6", px: { xs: 2, md: 3 }, py: 2.5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            {project ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={backToProjects}
                  sx={{ fontFamily: FONT, textTransform: "none", px: 0.5, minWidth: 0 }}
                >
                  All projects
                </Button>
              </Box>
            ) : null}
            <Typography sx={TITLE_SX}>
              {project ? projectTitle : "AI Usage"}
              {project && project !== "none" ? (
                <Box component="span" sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 400, color: "#64748B", ml: 1 }}>
                  ID {project}
                </Box>
              ) : null}
            </Typography>
            <Typography sx={SUBTITLE_SX}>
              {project
                ? "Every LLM call for this project, gate by gate. Open a run to see each call's prompt and response."
                : "Every LLM call ValidAIte makes, by project. Open a project to see its calls gate by gate."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {status && !status.enabled ? (
              <Chip label="Recording is off (OBS_ENABLED)" sx={{ fontFamily: FONT, bgcolor: "#FEF3C7", color: "#B45309" }} />
            ) : null}
            {status?.modelOverride ? (
              <Chip label={`Model override: ${status.modelOverride}`} sx={{ fontFamily: FONT, bgcolor: "#EFF6FF", color: "#005BF0" }} />
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
          <PeriodSelect days={days} onChange={setDays} />
          <Button variant="outlined" onClick={load} sx={{ fontFamily: FONT, textTransform: "none" }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ px: { xs: 2, md: 3 }, pt: 2, fontSize: 13, color: "#DE350B", fontWeight: 600, fontFamily: FONT }}>
          {error}
        </Typography>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : data?.view === "projects" ? (
          <ProjectsView data={data} onOpen={openProject} />
        ) : data?.view === "project" ? (
          <ProjectView data={data} onOpenRun={setOpenRunId} />
        ) : null}
      </Box>

      <RunDrawer runId={openRunId} onClose={closeRun} />
    </Box>
  );
}
